import { HttpClient } from '@angular/common/http'
import { inject } from '@angular/core'
import { CanActivateFn, Router } from '@angular/router'
import { Store } from '@ngxs/store'
import { catchError, map, of } from 'rxjs'
import { ENV_TOKEN } from '../../app.config'
import { PflegeboxAbgabewegOption } from '../../model/selfservice.model'
import { AppState } from '../../state/app.state'

/**
 * Schiebt die Frage „Versand oder Abholung?" vor den Anlage-Stepper, wenn es etwas zu waehlen gibt.
 *
 * Der gewaehlte Abgabeweg bestimmt das **Sortiment** — also welche Produkte der naechste Schritt
 * ueberhaupt anbietet. Wird er erst nachtraeglich gesetzt, hat man aus einer Liste gewaehlt, die
 * danach eine andere ist.
 *
 * Drei Entscheidungen, die hier drinstecken:
 *
 * - **Ein `aw` aus der URL zaehlt NICHT als Entscheidung — ausser im eingebetteten Rahmen.** Der
 *   Leistungserbringer haengt `aw` an jeden Link, den er der Patient:in gibt; als Entscheidung
 *   gewertet kaeme die Frage nie. Auf der Kartenseite belegt er deshalb nur vor. **Im iframe der
 *   Leistungserbringer-App (`embedded=true`) ist er dagegen die Entscheidung**: dort steht die
 *   Button-Group ueber dem Fenster, und ein zweiter Schalter darin koennte davon abweichen — beide
 *   Staende liefen auseinander, ohne dass einer der beiden es merkt.
 * - **Genau ein angebotener Weg = keine Frage.** Ein Haus, das nur versendet, haette hier nur einen
 *   Klick ohne Alternative.
 * - **Faellt der Abruf um, geht es weiter.** Waehrend eines Deploy-Fensters kann das Frontend vor
 *   dem Backend stehen (404 auf den neuen Endpunkt); der Bestellfluss darf daran nicht sterben —
 *   das Backend faellt dann auf den Weg zurueck, den das Haus selbst fuehrt.
 * - **KEIN angebotener Weg ist dagegen eine Antwort, kein Ausfall.** Eine erfolgreiche, aber leere
 *   Liste heisst: dieses Haus hat Pflegehilfsmittel gar nicht gepflegt. Bis 09/2026 lief die
 *   Patient:in trotzdem in den Stepper, dessen Produktabruf dann mit 500 endete (PROD, drei
 *   Haeuser in zwei Wochen) — sichtbar als halb aufgebaute Seite mit einer Fehlerkarte im
 *   Karussell. Der Unterschied zum Punkt darueber ist genau der Statuscode: `catchError` bleibt
 *   fuer Netz und 404, die leere Liste fuehrt auf eine eigene Seite.
 *
 * Die Query-Params liest der Guard SELBST aus dem Snapshot: er laeuft vor der `PhmAuthComponent`,
 * die den App-State aus der URL fuellt — der State ist hier also noch leer.
 */
export const abgabewegAuswahlGuard: CanActivateFn = (route) => {
  const store = inject(Store)
  const router = inject(Router)
  const http = inject(HttpClient)
  const apiUrl = inject(ENV_TOKEN).api.url

  const sanitaetshausId = route.queryParamMap.get('s')?.trim()
  if (!sanitaetshausId || store.selectSnapshot(AppState.getState).abgabewegBestaetigt) {
    return true
  }

  // Der eingebettete Rahmen hat den Weg bereits gewaehlt: seine Button-Group setzt `aw` und laedt
  // das Fenster damit neu. Fehlt `aw` trotz `embedded`, ist nichts vorgegeben — dann bleibt die Frage.
  const eingebettet = route.queryParamMap.get('embedded')?.trim().toLowerCase() === 'true'
  if (eingebettet && route.queryParamMap.get('aw')?.trim()) {
    return true
  }

  const partnerId = route.queryParamMap.get('sp')?.trim()
  return http
    .get<PflegeboxAbgabewegOption[]>(`${apiUrl}/pflegebox/abgabewege/${sanitaetshausId}`, {
      params: partnerId ? { externalPartnerId: partnerId } : {},
    })
    .pipe(
      map((wege) => {
        if (wege.length === 0) {
          return router.createUrlTree(['/phm/nicht-angeboten'], { queryParams: route.queryParams })
        }
        return wege.length > 1 ? router.createUrlTree(['/phm/auswahl/abgabeweg'], { queryParams: route.queryParams }) : true
      }),
      catchError(() => of(true)),
    )
}
